import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Copy, ExternalLink, FilterX, Loader2, Pencil, Plus, Power, RotateCcw, Search, Trash2,
} from "lucide-react";
import { api, fmt, apiError } from "@/lib/api";
import ProductEditor from "./ProductEditor";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "live", label: "Live" },
  { id: "disabled", label: "Disabled" },
  { id: "deleted", label: "Deleted" },
];

function statusOf(p) {
  if (p.is_deleted) return "deleted";
  return p.active ? "live" : "disabled";
}

function StatusBadge({ p, suffix = "" }) {
  const s = statusOf(p);
  const cls = s === "live" ? "text-emerald-400" : s === "deleted" ? "text-red-400" : "text-neutral-500";
  return <span className={`text-xs font-bold ${cls}`} data-testid={`status-${p.id}${suffix}`}>{s === "live" ? "Live" : s === "deleted" ? "Deleted" : "Disabled"}</span>;
}

export default function AdminProducts() {
  const [data, setData] = useState(null); // {products, total, page, pages, counts}
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [stock, setStock] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [taxonomy, setTaxonomy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [purgeArmed, setPurgeArmed] = useState(false);
  const [purging, setPurging] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setTaxonomy(data)).catch(() => {});
  }, []);

  const load = useCallback((pg = page, query = q, st = filter, cat = categoryId, sub = subcategoryId,
                           br = brandId, stk = stock, so = sort) => {
    const params = new URLSearchParams({ page: String(pg), page_size: "20", sort: so });
    if (query.trim()) params.set("q", query.trim());
    if (st && st !== "all") params.set("status", st);
    if (cat) params.set("category_id", cat);
    if (sub) params.set("subcategory_id", sub);
    if (br) params.set("brand_id", br);
    if (stk) params.set("stock", stk);
    api.get(`/admin/products?${params}`).then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load products"));
  }, [page, q, filter, categoryId, subcategoryId, brandId, stock, sort]);
  useEffect(load, [load]);
  useEffect(() => { setSelected(new Set()); setPurgeArmed(false); }, [filter, q, page]);

  const onSearch = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(1); load(1, v); }, 350);
  };
  const applyFilter = (setter, key) => (e) => {
    const v = e.target.value;
    setter(v);
    setPage(1);
    load(1, q, key === "status" ? v : filter, key === "cat" ? v : categoryId,
         key === "sub" ? v : subcategoryId, key === "brand" ? v : brandId,
         key === "stock" ? v : stock, key === "sort" ? v : sort);
  };
  const clearFilters = () => {
    setQ(""); setFilter("all"); setCategoryId(""); setSubcategoryId(""); setBrandId("");
    setStock(""); setSort("newest"); setPage(1);
    load(1, "", "all", "", "", "", "", "newest");
  };
  const hasFilters = q || filter !== "all" || categoryId || subcategoryId || brandId || stock || sort !== "newest";

  const filtered = data?.products || [];
  const counts = data?.counts || { all: 0, live: 0, disabled: 0, deleted: 0 };

  const toggleSelect = (id) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const deletedVisible = filtered.filter((p) => p.is_deleted);
  const allSelected = deletedVisible.length > 0 && deletedVisible.every((p) => selected.has(p.id));

  const purgeSelected = async () => {
    if (!purgeArmed) {
      setPurgeArmed(true);
      setTimeout(() => setPurgeArmed(false), 4000);
      return;
    }
    setPurging(true);
    const ids = [...selected];
    let done = 0;
    for (const id of ids) {
      try {
        await api.delete(`/admin/products/${id}/permanent`);
        done += 1;
      } catch { /* row-level errors surfaced via count below */ }
    }
    toast.success(done === ids.length ? `${done} product${done === 1 ? "" : "s"} permanently deleted` : `${done} of ${ids.length} deleted — some failed`);
    setSelected(new Set());
    setPurgeArmed(false);
    setPurging(false);
    load();
  };

  const run = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(apiError(err, "Action failed"));
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  const duplicate = (p) =>
    run(p.id, () => api.post(`/admin/products/${p.id}/duplicate`, {}), `Duplicated "${p.name}" as a disabled draft — review and publish it`);

  const toggleActive = (p) =>
    run(p.id, () => api.patch(`/admin/products/${p.id}/status`, { active: !p.active }), p.active ? `${p.name} disabled` : `${p.name} is live`);

  const softDelete = (p) => {
    if (confirmId !== p.id) {
      setConfirmId(p.id);
      setTimeout(() => setConfirmId((c) => (c === p.id ? null : c)), 3000);
      return;
    }
    run(p.id, () => api.delete(`/admin/products/${p.id}`), `${p.name} deleted (kept for past orders)`);
  };

  const restore = (p) =>
    run(p.id, () => api.post(`/admin/products/${p.id}/restore`), `${p.name} restored as disabled`);

  const purgeOne = (p) => {
    const key = `${p.id}-purge`;
    if (confirmId !== key) {
      setConfirmId(key);
      setTimeout(() => setConfirmId((c) => (c === key ? null : c)), 3000);
      return;
    }
    run(p.id, () => api.delete(`/admin/products/${p.id}/permanent`), `${p.name} permanently deleted`);
  };

  const iconBtn = "flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors disabled:opacity-40";

  const Actions = ({ p, suffix = "" }) => {
    const busy = busyId === p.id;
    if (p.is_deleted) {
      return (
        <>
          <button data-testid={`admin-restore-${p.id}${suffix}`} onClick={() => restore(p)} disabled={busy} className={`${iconBtn} hover:border-emerald-500/50 hover:text-emerald-400`} aria-label="Restore" title="Restore as disabled">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          </button>
          <button
            data-testid={`admin-purge-${p.id}${suffix}`}
            onClick={() => purgeOne(p)}
            disabled={busy}
            className={`${iconBtn} ${confirmId === `${p.id}-purge` ? "border-red-500/70 bg-red-500/15 text-red-400" : "hover:border-red-400/50 hover:text-red-400"}`}
            aria-label="Delete forever" title={confirmId === `${p.id}-purge` ? "Click again — permanent, no undo" : "Delete forever"}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </>
      );
    }
    return (
      <>
        <button data-testid={`admin-view-${p.id}${suffix}`} onClick={() => window.open(`/shop/product/${p.id}`, "_blank")} className={`${iconBtn} hover:border-emerald-500/50 hover:text-emerald-400`} aria-label="View in shop" title="View in shop">
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
        <button data-testid={`admin-edit-${p.id}${suffix}`} onClick={() => { setEditing(p); setModalOpen(true); }} className={`${iconBtn} hover:border-amber-500/50 hover:text-amber-400`} aria-label="Edit" title="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button data-testid={`admin-duplicate-${p.id}${suffix}`} onClick={() => duplicate(p)} disabled={busy} className={`${iconBtn} hover:border-amber-500/50 hover:text-amber-400`} aria-label="Duplicate" title="Duplicate as draft">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <button data-testid={`admin-toggle-${p.id}${suffix}`} onClick={() => toggleActive(p)} disabled={busy} className={`${iconBtn} ${p.active ? "hover:border-neutral-500/50" : "border-emerald-500/30 text-emerald-500 hover:border-emerald-500/60"}`} aria-label={p.active ? "Disable" : "Enable"} title={p.active ? "Disable" : "Enable"}>
          <Power className="h-3.5 w-3.5" />
        </button>
        <button
          data-testid={`admin-delete-${p.id}${suffix}`}
          onClick={() => softDelete(p)}
          disabled={busy}
          className={`${iconBtn} ${confirmId === p.id ? "border-red-500/70 bg-red-500/15 text-red-400" : "hover:border-red-400/50 hover:text-red-400"}`}
          aria-label="Delete" title={confirmId === p.id ? "Click again to confirm" : "Delete (recoverable)"}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </>
    );
  };

  return (
    <div data-testid="admin-products">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4">
          <Search className="h-4 w-4 text-neutral-500" />
          <input data-testid="admin-products-search" value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search products or SKU…" className="h-11 w-44 bg-transparent text-sm text-white placeholder:text-neutral-600 focus:outline-none sm:w-56" />
        </div>
        <button
          data-testid="admin-add-product"
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400"
        >
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="mb-5 flex gap-2 overflow-x-auto" data-testid="admin-products-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => { setFilter(f.id); setPage(1); load(1, q, f.id, categoryId, subcategoryId, brandId, stock, sort); }}
            className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
              filter === f.id ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400 hover:text-white"
            }`}
          >
            {f.label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filter === f.id ? "bg-amber-500 text-black" : "bg-white/10"}`}>{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="admin-products-adv-filters">
        <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setSubcategoryId(""); setPage(1); load(1, q, filter, e.target.value, "", brandId, stock, sort); }}
          data-testid="filter-category"
          className="h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white focus:border-amber-500/60 focus:outline-none">
          <option value="">All categories</option>
          {(taxonomy?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={subcategoryId} onChange={applyFilter(setSubcategoryId, "sub")} data-testid="filter-subcategory"
          disabled={!categoryId}
          className="h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white focus:border-amber-500/60 focus:outline-none disabled:opacity-40">
          <option value="">All subcategories</option>
          {(taxonomy?.categories.find((c) => c.id === categoryId)?.subcategories || []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select value={brandId} onChange={applyFilter(setBrandId, "brand")} data-testid="filter-brand"
          className="h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white focus:border-amber-500/60 focus:outline-none">
          <option value="">All brands</option>
          {(taxonomy?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={stock} onChange={applyFilter(setStock, "stock")} data-testid="filter-stock"
          className="h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white focus:border-amber-500/60 focus:outline-none">
          <option value="">Any stock</option>
          <option value="in">In stock</option>
          <option value="out">Out of stock</option>
        </select>
        <select value={sort} onChange={applyFilter(setSort, "sort")} data-testid="filter-sort"
          className="h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white focus:border-amber-500/60 focus:outline-none">
          <option value="newest">Newest first</option>
          <option value="name">Name A–Z</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
          <option value="stock_asc">Stock: low to high</option>
          <option value="stock_desc">Stock: high to low</option>
        </select>
        {hasFilters && (
          <button onClick={clearFilters} data-testid="filter-clear"
            className="flex h-10 items-center gap-1.5 rounded-full border border-red-500/40 px-4 text-xs font-bold text-red-300 hover:bg-red-500/10">
            <FilterX className="h-3.5 w-3.5" /> Clear Filters
          </button>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] py-16 text-center text-sm text-neutral-500" data-testid="admin-products-empty">
          No products match this view.
        </div>
      ) : (
        <>
          {filter === "deleted" && (
            <div className="mb-3 flex items-center justify-between rounded-xl border border-red-500/20 bg-red-500/[0.04] px-4 py-3" data-testid="purge-bar">
              <p className="text-xs text-neutral-400">
                {selected.size > 0 ? `${selected.size} selected` : "Select deleted products to remove them forever"}
              </p>
              <button
                data-testid="purge-selected-btn"
                onClick={purgeSelected}
                disabled={selected.size === 0 || purging}
                className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold transition-colors disabled:opacity-40 ${
                  purgeArmed ? "bg-red-500 text-white" : "border border-red-500/50 text-red-400 hover:bg-red-500/10"
                }`}
              >
                {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                {purgeArmed ? "Click again — permanent, no undo" : `Delete Forever${selected.size ? ` (${selected.size})` : ""}`}
              </button>
            </div>
          )}
          <div className="hidden overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA] md:block">
            <table className="w-full min-w-[860px] text-left text-sm" data-testid="admin-products-table">
              <thead>
                <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  {filter === "deleted" && (
                    <th className="w-10 px-4 py-4">
                      <input type="checkbox" data-testid="purge-select-all" checked={allSelected}
                        onChange={() => setSelected(allSelected ? new Set() : new Set(deletedVisible.map((p) => p.id)))}
                        className="h-4 w-4 accent-red-500" aria-label="Select all deleted" />
                    </th>
                  )}
                  <th className="px-5 py-4">Product</th>
                  <th className="px-5 py-4">Category</th>
                  <th className="px-5 py-4">Price</th>
                  <th className="px-5 py-4">Stock</th>
                  <th className="px-5 py-4">Flags</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className={`border-b border-white/[0.04] ${p.is_deleted ? "opacity-50" : ""} ${selected.has(p.id) ? "bg-red-500/[0.06]" : ""}`} data-testid={`admin-product-row-${p.id}`}>
                    {filter === "deleted" && (
                      <td className="px-4 py-3.5">
                        <input type="checkbox" data-testid={`purge-select-${p.id}`} checked={selected.has(p.id)}
                          onChange={() => toggleSelect(p.id)} className="h-4 w-4 accent-red-500" aria-label={`Select ${p.name}`} />
                      </td>
                    )}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <img src={(p.images || [""])[0]} alt="" className="h-10 w-10 rounded-lg object-cover" loading="lazy" />
                        <div>
                          <p className="font-semibold text-white">{p.name}</p>
                          <p className="font-mono text-[10px] text-neutral-600">
                            {p.variants.slice(0, 2).map((v) => v.sku).join(" · ")}{p.variants.length > 2 && ` +${p.variants.length - 2}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400">{p.category}{p.subcategory ? <span className="text-neutral-600"> › {p.subcategory}</span> : ""}</td>
                    <td className="px-5 py-3.5 text-neutral-300">{fmt(p.price_min)}<span className="text-[10px] text-neutral-600">/{p.unit || "pc"}</span>{p.price_max > p.price_min && ` – ${fmt(p.price_max)}`}</td>
                    <td className={`px-5 py-3.5 font-bold ${p.total_stock <= 0 ? "text-red-400" : "text-neutral-300"}`}>{p.total_stock}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-1.5">
                          {p.new_arrival && <span className="rounded border border-emerald-500/40 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">NEW</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3.5"><StatusBadge p={p} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-2"><Actions p={p} /></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden" data-testid="admin-products-cards">
            {filtered.map((p) => (
              <div key={p.id} className={`rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-4 ${p.is_deleted ? "opacity-50" : ""} ${selected.has(p.id) ? "border-red-500/40" : ""}`} data-testid={`admin-product-card-${p.id}`}>
                <div className="flex items-start gap-3">
                  {filter === "deleted" && (
                    <input type="checkbox" data-testid={`purge-select-${p.id}-card`} checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)} className="mt-4 h-4 w-4 shrink-0 accent-red-500" aria-label={`Select ${p.name}`} />
                  )}
                  <img src={(p.images || [""])[0]} alt="" className="h-14 w-14 rounded-xl object-cover" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-white">{p.name}</p>
                    <p className="mt-0.5 text-xs text-neutral-500">{p.category}{p.subcategory ? ` › ${p.subcategory}` : ""} · {fmt(p.price_min)}/{p.unit || "pc"}{p.price_max > p.price_min && ` – ${fmt(p.price_max)}`}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <StatusBadge p={p} suffix="-card" />
                      <span className={`text-xs font-bold ${p.total_stock <= 0 ? "text-red-400" : "text-neutral-400"}`}>Stock {p.total_stock}</span>
                      {p.new_arrival && <span className="rounded border border-emerald-500/40 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">NEW</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2 border-t border-white/[0.05] pt-3"><Actions p={p} suffix="-card" /></div>
              </div>
            ))}
          </div>
        </>
      )}

          {data && data.pages > 1 && (
            <div className="mt-5 flex items-center justify-between" data-testid="admin-products-pagination">
              <p className="text-xs text-neutral-500">{data.total} product(s) · page {data.page} of {data.pages}</p>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => { setPage((p) => p - 1); }} data-testid="products-prev"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button disabled={page >= data.pages} onClick={() => { setPage((p) => p + 1); }} data-testid="products-next"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <AnimatePresence>
        {modalOpen && (
          <ProductEditor
            product={editing}
            onClose={() => setModalOpen(false)}
            onSaved={() => { setModalOpen(false); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
